import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';

const UserManagement = ({ token, currentUser }) => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('user');
  const [addUserError, setAddUserError] = useState('');
  const [addUserLoading, setAddUserLoading] = useState(false);

  const loadUsers = useCallback(async () => {
    try {
      const data = await api.getUsers(token);
      setUsers(data);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  }, [token]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    try {
      await api.resetUserPassword(token, selectedUser.id, resetPassword);
      alert('Password reset successfully!');
      setResetPassword('');
      setShowResetPassword(false);
      setSelectedUser(null);
    } catch (error) {
      alert(error.message || 'Failed to reset password');
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setAddUserError('');
    setAddUserLoading(true);
    try {
      await api.registerUser(token, newUsername.trim(), newPassword, newRole);
      setNewUsername('');
      setNewPassword('');
      setNewRole('user');
      setShowAddUser(false);
      loadUsers();
    } catch (error) {
      setAddUserError(error.message || 'Failed to create user');
    } finally {
      setAddUserLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      await api.deleteUser(token, userId);
      alert('User deleted successfully!');
      loadUsers();
    } catch (error) {
      alert(error.message || 'Failed to delete user');
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto animate-fadeIn space-y-6 pb-24">
      {/* Header */}
      <div className="bg-surface-container border border-outline-variant/20 rounded-3xl p-6 sm:p-8 shadow-lg flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
        <div>
          <h2 className="text-on-background text-2xl font-lexend font-bold flex items-center gap-3 mb-1">
            <span className="material-symbols-outlined text-primary text-3xl">manage_accounts</span>
            User Management
          </h2>
          <p className="text-stone-600 font-manrope text-sm">Manage club access, player accounts, and permissions.</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowAddUser(true)}
            className="flex items-center gap-2 bg-primary text-on-primary font-manrope font-bold text-sm uppercase tracking-widest px-5 py-3 rounded-xl hover:bg-primary/90 transition-all shadow-sm active:scale-95"
          >
            <span className="material-symbols-outlined text-[18px]">person_add</span>
            Add User
          </button>
          <div className="bg-background border border-outline-variant/50 px-4 py-2 rounded-xl flex items-center gap-3 shadow-inner">
              <span className="material-symbols-outlined text-primary">verified_user</span>
              <span className="text-on-background font-lexend font-bold text-sm">{users.length} Active Accounts</span>
          </div>
        </div>
      </div>

      {/* Users Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {users.map((user) => {
          const isSuperAdmin = user.username === 'admin';
          const isCurrentUser = user.id === currentUser.id;
          const isCurrentUserSuperAdmin = currentUser.username === 'admin';
          
          return (
            <div key={user.id} className="bg-surface-container border border-outline-variant/20 rounded-3xl p-6 shadow-lg hover:bg-surface-container-high transition-all duration-300 group">
              <div className="flex items-start gap-4 mb-6 border-b border-outline-variant/20 pb-6">
                <div className="relative">
                    <div className="w-14 h-14 bg-surface-container-highest border border-outline-variant/30 rounded-2xl flex items-center justify-center shadow-inner">
                        <span className="text-on-background font-lexend font-bold text-xl uppercase">
                            {user.username.charAt(0)}
                        </span>
                    </div>
                    {isSuperAdmin && (
                        <div className="absolute -bottom-2 -right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center border-2 border-surface-container shadow-lg">
                            <span className="material-symbols-outlined text-on-primary text-[12px] font-black">shield</span>
                        </div>
                    )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-on-background font-lexend font-bold text-lg truncate">{user.username}</h3>
                  </div>
                  
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`text-[9px] px-2.5 py-1 rounded-lg font-bold font-manrope uppercase tracking-wider border ${
                        user.role === 'admin' 
                        ? 'bg-rose-500/10 text-rose-700 border-rose-500/20' 
                        : 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20'
                    }`}>
                        {user.role}
                    </span>
                    {isCurrentUser && (
                        <span className="text-[9px] px-2.5 py-1 rounded-lg font-bold font-manrope uppercase tracking-wider bg-background text-stone-600 border border-outline-variant/50">
                            You
                        </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                {/* Show reset button only if: current user is super admin OR it's their own account */}
                {(isCurrentUserSuperAdmin || isCurrentUser) && (
                  <button
                    onClick={() => {
                      setSelectedUser(user);
                      setShowResetPassword(true);
                    }}
                    className="flex-1 bg-background border border-outline-variant/30 text-amber-700 px-3 py-3 rounded-xl hover:bg-amber-400/10 hover:border-amber-400/30 transition-all font-lexend font-bold text-xs uppercase tracking-widest flex items-center justify-center gap-2 shadow-sm active:scale-95"
                  >
                    <span className="material-symbols-outlined text-[16px]">key</span>
                    Reset
                  </button>
                )}
                
                {/* Show delete button only if: not super admin AND not current user AND current user is super admin */}
                {!isSuperAdmin && !isCurrentUser && isCurrentUserSuperAdmin && (
                  <button
                    onClick={() => handleDeleteUser(user.id)}
                    className="w-12 bg-background border border-outline-variant/30 text-rose-700 rounded-xl hover:bg-rose-500/10 hover:border-rose-500/30 transition-all flex items-center justify-center shadow-sm active:scale-95"
                    title="Delete Account"
                  >
                    <span className="material-symbols-outlined text-[18px]">delete_forever</span>
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add User Modal */}
      {showAddUser && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-md flex items-center justify-center p-4 z-50 animate-fadeIn">
          <div className="bg-surface-container border border-outline-variant/30 rounded-3xl shadow-2xl p-8 w-full max-w-md relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none"></div>

            <div className="flex justify-between items-center mb-6 relative z-10">
              <h3 className="text-2xl font-lexend font-bold text-on-background flex items-center gap-2">
                  <span className="material-symbols-outlined text-primary">person_add</span>
                  Add User
              </h3>
              <button
                onClick={() => { setShowAddUser(false); setAddUserError(''); }}
                className="p-2 hover:bg-surface-container-high rounded-xl transition-colors text-stone-600 hover:text-on-background active:scale-95"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <form onSubmit={handleAddUser} className="space-y-5 relative z-10">
              <div className="space-y-2">
                <label className="block text-stone-700 font-manrope text-[10px] font-bold uppercase tracking-widest pl-1">Username</label>
                <input
                  type="text"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  placeholder="Enter username"
                  required
                  className="w-full bg-background border border-outline-variant/50 rounded-xl px-4 py-3 text-on-background font-manrope placeholder:text-stone-600 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-stone-700 font-manrope text-[10px] font-bold uppercase tracking-widest pl-1">Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter password"
                  required
                  minLength={6}
                  className="w-full bg-background border border-outline-variant/50 rounded-xl px-4 py-3 text-on-background font-manrope placeholder:text-stone-600 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-stone-700 font-manrope text-[10px] font-bold uppercase tracking-widest pl-1">Role</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setNewRole('user')}
                    className={`py-3 rounded-xl font-manrope font-bold text-sm uppercase tracking-widest border transition-all ${
                      newRole === 'user'
                        ? 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30'
                        : 'bg-background text-stone-600 border-outline-variant/50 hover:bg-surface-container-high'
                    }`}
                  >
                    User
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewRole('admin')}
                    className={`py-3 rounded-xl font-manrope font-bold text-sm uppercase tracking-widest border transition-all ${
                      newRole === 'admin'
                        ? 'bg-rose-500/10 text-rose-700 border-rose-500/30'
                        : 'bg-background text-stone-600 border-outline-variant/50 hover:bg-surface-container-high'
                    }`}
                  >
                    Admin
                  </button>
                </div>
              </div>

              {addUserError && (
                <p className="text-error font-manrope text-sm">{addUserError}</p>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowAddUser(false); setAddUserError(''); }}
                  className="flex-1 bg-background border border-outline-variant/50 text-stone-600 hover:text-on-background py-4 rounded-xl font-lexend font-bold text-xs uppercase tracking-widest transition-colors active:scale-95"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addUserLoading}
                  className="flex-[2] bg-primary hover:bg-primary/90 text-on-primary py-4 rounded-xl font-lexend font-bold text-xs uppercase tracking-widest transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <span className="material-symbols-outlined text-[16px]">check_circle</span>
                  {addUserLoading ? 'Adding...' : 'Add User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPassword && selectedUser && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-md flex items-center justify-center p-4 z-50 animate-fadeIn">
          <div className="bg-surface-container border border-outline-variant/30 rounded-3xl shadow-2xl p-8 w-full max-w-md relative overflow-hidden">
            {/* Background Glow */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none"></div>

            <div className="flex justify-between items-center mb-6 relative z-10">
              <h3 className="text-2xl font-lexend font-bold text-on-background flex items-center gap-2">
                  <span className="material-symbols-outlined text-primary">lock_reset</span>
                  Reset Password
              </h3>
              <button
                onClick={() => {
                  setShowResetPassword(false);
                  setSelectedUser(null);
                  setResetPassword('');
                }}
                className="p-2 hover:bg-surface-container-high rounded-xl transition-colors text-stone-600 hover:text-on-background active:scale-95"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <p className="text-stone-600 font-manrope text-sm mb-6 relative z-10">
              Set a new password for <span className="text-primary font-bold">{selectedUser.username}</span>.
            </p>

            <form onSubmit={handleResetPassword} className="space-y-6 relative z-10">
              <div className="space-y-2">
                <label className="block text-stone-700 font-manrope text-[10px] font-bold uppercase tracking-widest pl-1">New Password</label>
                <div className="relative group">
                    <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-stone-700 group-focus-within:text-primary transition-colors">password</span>
                    <input
                    type="password"
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    className="w-full bg-background border border-outline-variant/50 rounded-xl pl-12 pr-4 py-4 text-on-background font-manrope placeholder:text-stone-800 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all shadow-inner"
                    required
                    minLength={6}
                    placeholder="Enter new code"
                    />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowResetPassword(false);
                    setSelectedUser(null);
                    setResetPassword('');
                  }}
                  className="flex-1 bg-background border border-outline-variant/50 text-stone-600 hover:text-on-background py-4 rounded-xl font-lexend font-bold text-xs uppercase tracking-widest transition-colors active:scale-95"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-[2] bg-primary hover:bg-primary/90 text-on-primary py-4 rounded-xl font-lexend font-bold text-xs uppercase tracking-widest transition-all active:scale-95 shadow-[0_0_15px_rgba(30,122,70,0.2)] flex items-center justify-center gap-2"
                >
                  <span className="material-symbols-outlined text-[16px]">check_circle</span>
                  Confirm Reset
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
